/**
 * Light/dark theme toggle. The mockup gates dark mode behind
 * `html[data-theme="dark"]` (`mockup.css` line 71+), so we just toggle that
 * attribute and persist the choice. The initial value is restored before
 * first paint by an inline `<script>` in `index.html` to avoid a flash of
 * the wrong theme.
 */
export type Theme = 'light' | 'dark';

const LS_KEY = 'smt_theme';

export function getTheme(): Theme {
  // Dark is the default — `data-theme="dark"` is set by the inline boot
  // script in index.html unless the user explicitly chose light.
  return document.documentElement.getAttribute('data-theme') === 'dark' ? 'dark' : 'light';
}

export function setTheme(theme: Theme): void {
  if (theme === 'dark') {
    document.documentElement.setAttribute('data-theme', 'dark');
  } else {
    // Light = absence of the dark attribute (mockup parity).
    document.documentElement.removeAttribute('data-theme');
  }
  try {
    // Persist explicit choices so the boot script can honour them on
    // reload. Default-to-dark is encoded as the absence of any value.
    localStorage.setItem(LS_KEY, theme);
  } catch {
    /* ignore */
  }
  syncToggleState();
}

export function bindThemeToggle(): void {
  const group = document.getElementById('themeToggle');
  if (!group) return;
  syncToggleState();
  group.querySelectorAll<HTMLButtonElement>('button[data-theme-target]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const target = btn.getAttribute('data-theme-target') as Theme | null;
      if (!target) return;
      setTheme(target);
    });
  });
}

function syncToggleState(): void {
  const cur = getTheme();
  document
    .querySelectorAll<HTMLButtonElement>('#themeToggle button[data-theme-target]')
    .forEach((btn) => {
      btn.classList.toggle('active', btn.getAttribute('data-theme-target') === cur);
    });
}
